/**
 * Custom Effects Service
 * Allows admins to create custom effects that can be linked to promo codes
 * 
 * Features:
 * - Create unlimited custom effects
 * - Multiple effect types (token boost, feature unlock, UI changes, etc.)
 * - Link multiple effects to promo codes
 * - Effect stacking and combinations
 * - Time-based effects with customizable duration
 */

import { User, PromoCode } from '../types';

// ==================== Types ====================

export type EffectType = 
    | 'token_boost'           // Add tokens
    | 'token_multiplier'      // Multiply token refresh
    | 'unlimited_tokens'      // Unlimited tokens for duration
    | 'feature_unlock'        // Unlock specific features
    | 'tier_upgrade'          // Temporary tier upgrade
    | 'tier_promote'          // Promote rank 1 level
    | 'tier_demote'           // Demote rank 1 level
    | 'set_rank'              // Set specific rank
    | 'token_capacity_boost'  // Increase token capacity
    | 'token_refresh_boost'   // Increase token refresh rate
    | 'discount'              // Discount on purchases
    | 'ui_theme'              // Unlock special UI theme
    | 'badge'                 // Grant special badge
    | 'priority_queue'        // Priority in request queue
    | 'extended_context'      // Extended chat context
    | 'custom_persona'        // Custom AI persona
    | 'bonus_exports'         // Extra exports per day
    | 'bonus_quizzes'         // Extra quizzes per day
    | 'bonus_exams'           // Extra exams per day
    | 'early_access'          // Early access to features
    | 'custom';               // Fully custom effect

export interface CustomEffect {
    id: string;
    name: string;
    description: string;
    type: EffectType;
    icon?: string;           // Emoji or icon name
    color?: string;          // Display color
    
    // Effect configuration
    config: EffectConfig;
    
    // Duration (in minutes, -1 for permanent)
    durationMinutes: number;
    
    // Stacking rules
    stackable: boolean;      // Can stack with same effect
    stackLimit?: number;     // Max stacks
    
    // Visibility
    showInUI: boolean;       // Show effect indicator in UI
    notifyUser: boolean;     // Send notification when activated
    
    // Metadata
    createdAt: number;
    createdBy?: string;
    isActive: boolean;
}

export interface EffectConfig {
    // Token effects
    tokenAmount?: number;          // For token_boost
    tokenMultiplier?: number;      // For token_multiplier
    tokenCapacityBoost?: number;   // For token_capacity_boost (flat or percentage)
    tokenCapacityBoostType?: 'flat' | 'percent'; // Type of capacity boost
    tokenRefreshBoost?: number;    // For token_refresh_boost (flat or percentage)
    tokenRefreshBoostType?: 'flat' | 'percent'; // Type of refresh boost
    
    // Feature effects
    featureIds?: string[];         // For feature_unlock
    
    // Tier/Rank effects
    targetTier?: 'standard' | 'premium' | 'max'; // For tier_upgrade or set_rank
    isPermanent?: boolean;         // For permanent rank changes
    
    // Discount effects
    discountPercent?: number;      // For discount
    discountAmount?: number;       // Fixed discount
    
    // UI effects
    themeId?: string;              // For ui_theme
    
    // Badge effects
    badgeId?: string;              // For badge
    badgeTitle?: string;
    badgeIcon?: string;
    
    // Bonus effects
    bonusAmount?: number;          // For bonus effects
    
    // Custom effects
    customData?: Record<string, any>;
    
    // Display
    displayMessage?: string;       // Message to show user
    animationType?: 'confetti' | 'sparkle' | 'glow' | 'shake';
}

export interface UserEffect {
    effectId: string;
    activatedAt: number;
    expiresAt: number | null;  // null for permanent
    stacks: number;
    source: 'promo' | 'admin' | 'reward' | 'system';
    sourceId?: string;         // Promo code, admin user, etc.
}

export interface EffectActivationResult {
    success: boolean;
    message: string;
    effect?: CustomEffect;
    expiresAt?: number;
    displayAnimation?: string;
}

// ==================== Default Effects ====================

export const DEFAULT_EFFECTS: Omit<CustomEffect, 'createdAt' | 'createdBy'>[] = [
    {
        id: 'unlimited_tokens_1h',
        name: 'Unlimited Tokens (1 Hour)',
        description: 'Enjoy unlimited tokens for 1 hour',
        type: 'unlimited_tokens',
        icon: '🚀',
        color: '#8b5cf6',
        config: {
            displayMessage: '🚀 Unlimited tokens activated for 1 hour!'
        },
        durationMinutes: 60,
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'unlimited_tokens_24h',
        name: 'Unlimited Tokens (24 Hours)',
        description: 'Enjoy unlimited tokens for 24 hours',
        type: 'unlimited_tokens',
        icon: '🚀',
        color: '#8b5cf6',
        config: {
            displayMessage: '🚀 Unlimited tokens activated for 24 hours!'
        },
        durationMinutes: 1440,
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'token_boost_500',
        name: '500 Token Boost',
        description: 'Instantly receive 500 bonus tokens',
        type: 'token_boost',
        icon: '⚡',
        color: '#f59e0b',
        config: {
            tokenAmount: 500,
            displayMessage: '⚡ You received 500 bonus tokens!',
            animationType: 'sparkle'
        },
        durationMinutes: -1,
        stackable: true,
        stackLimit: 10,
        showInUI: false,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'token_boost_1000',
        name: '1000 Token Boost',
        description: 'Instantly receive 1000 bonus tokens',
        type: 'token_boost',
        icon: '⚡',
        color: '#f59e0b',
        config: {
            tokenAmount: 1000,
            displayMessage: '⚡ You received 1000 bonus tokens!',
            animationType: 'sparkle'
        },
        durationMinutes: -1,
        stackable: true,
        stackLimit: 10,
        showInUI: false,
        notifyUser: true,
        isActive: true
    },
    {
        id: '2x_tokens_7d',
        name: '2x Token Multiplier (7 Days)',
        description: 'Double your token refresh rate for 7 days',
        type: 'token_multiplier',
        icon: '✨',
        color: '#10b981',
        config: {
            tokenMultiplier: 2,
            displayMessage: '✨ 2x token multiplier activated for 7 days!'
        },
        durationMinutes: 10080, // 7 days
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'premium_trial_7d',
        name: 'Premium Trial (7 Days)',
        description: 'Enjoy Premium features for 7 days',
        type: 'tier_upgrade',
        icon: '👑',
        color: '#f59e0b',
        config: {
            targetTier: 'premium',
            displayMessage: '👑 Premium trial activated for 7 days!'
        },
        durationMinutes: 10080,
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'max_trial_3d',
        name: 'MAX Trial (3 Days)',
        description: 'Experience MAX tier for 3 days',
        type: 'tier_upgrade',
        icon: '💎',
        color: '#a855f7',
        config: {
            targetTier: 'max',
            displayMessage: '💎 MAX trial activated for 3 days!'
        },
        durationMinutes: 4320,
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'priority_queue_24h',
        name: 'Priority Queue (24 Hours)',
        description: 'Skip the queue for 24 hours',
        type: 'priority_queue',
        icon: '🏃',
        color: '#06b6d4',
        config: {
            displayMessage: '🏃 Priority queue access activated!'
        },
        durationMinutes: 1440,
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'special_badge_vip',
        name: 'VIP Badge',
        description: 'Show off your VIP status',
        type: 'badge',
        icon: '⭐',
        color: '#f59e0b',
        config: {
            badgeId: 'vip',
            badgeTitle: 'VIP Member',
            badgeIcon: '⭐',
            displayMessage: '⭐ VIP badge added to your profile!'
        },
        durationMinutes: -1, // Permanent
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    },
    {
        id: 'early_access',
        name: 'Early Access Pass',
        description: 'Get early access to new features',
        type: 'early_access',
        icon: '🔮',
        color: '#8b5cf6',
        config: {
            displayMessage: '🔮 Early access activated!'
        },
        durationMinutes: 43200, // 30 days
        stackable: false,
        showInUI: true,
        notifyUser: true,
        isActive: true
    }
];

// ==================== Custom Effects Service ====================

class CustomEffectsService {
    private effects: Map<string, CustomEffect> = new Map();
    private userEffects: Map<string, UserEffect[]> = new Map();
    private listeners: Set<(effects: CustomEffect[]) => void> = new Set();

    constructor() {
        this.loadEffects();
    }

    /**
     * Initialize with default effects and load custom ones
     */
    private loadEffects(): void {
        try {
            // Load defaults
            DEFAULT_EFFECTS.forEach(effect => {
                this.effects.set(effect.id, {
                    ...effect,
                    createdAt: Date.now()
                });
            });

            // Load custom effects from storage
            const stored = localStorage.getItem('luca_custom_effects');
            if (stored) {
                const customEffects = JSON.parse(stored);
                customEffects.forEach((effect: CustomEffect) => {
                    this.effects.set(effect.id, effect);
                });
            }

            // Load user effects from storage
            const userEffectsStored = localStorage.getItem('luca_user_effects');
            if (userEffectsStored) {
                const parsed = JSON.parse(userEffectsStored);
                Object.entries(parsed).forEach(([userId, effects]) => {
                    this.userEffects.set(userId, effects as UserEffect[]);
                });
            }

            console.log('[CustomEffects] Loaded', this.effects.size, 'effects');
        } catch (error) {
            console.error('[CustomEffects] Failed to load effects:', error);
        }
    }

    /**
     * Save effects to storage
     */
    private saveEffects(): void {
        try {
            const customEffects = Array.from(this.effects.values())
                .filter(e => !DEFAULT_EFFECTS.some(d => d.id === e.id));
            localStorage.setItem('luca_custom_effects', JSON.stringify(customEffects));
        } catch (error) {
            console.error('[CustomEffects] Failed to save effects:', error);
        }
    }

    /**
     * Save user effects to storage
     */
    private saveUserEffects(): void {
        try {
            const userEffectsObj: Record<string, UserEffect[]> = {};
            this.userEffects.forEach((effects, userId) => {
                userEffectsObj[userId] = effects;
            });
            localStorage.setItem('luca_user_effects', JSON.stringify(userEffectsObj));
        } catch (error) {
            console.error('[CustomEffects] Failed to save user effects:', error);
        }
    }

    /**
     * Get all effects
     */
    getAllEffects(): CustomEffect[] {
        return Array.from(this.effects.values());
    }

    /**
     * Alias for getAllEffects (for compatibility)
     */
    getEffects(): CustomEffect[] {
        return this.getAllEffects();
    }

    /**
     * Get active effects only
     */
    getActiveEffects(): CustomEffect[] {
        return Array.from(this.effects.values()).filter(e => e.isActive);
    }

    /**
     * Get effect by ID
     */
    getEffect(id: string): CustomEffect | null {
        return this.effects.get(id) || null;
    }

    /**
     * Create new custom effect
     */
    createEffect(effect: Omit<CustomEffect, 'id' | 'createdAt'>): CustomEffect {
        const newEffect: CustomEffect = {
            ...effect,
            id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            createdAt: Date.now()
        };

        this.effects.set(newEffect.id, newEffect);
        this.saveEffects();
        this.notifyListeners();

        return newEffect;
    }

    /**
     * Add effect (for admin panel compatibility)
     */
    addEffect(effect: Partial<CustomEffect>, source?: string): CustomEffect {
        return this.createEffect(effect as Omit<CustomEffect, 'id' | 'createdAt'>);
    }

    /**
     * Update existing effect
     */
    updateEffect(id: string, updates: Partial<CustomEffect>): boolean {
        const effect = this.effects.get(id);
        if (!effect) return false;

        const updated = { ...effect, ...updates };
        this.effects.set(id, updated);
        this.saveEffects();
        this.notifyListeners();

        return true;
    }

    /**
     * Delete effect
     */
    deleteEffect(id: string): boolean {
        if (!this.effects.has(id)) return false;
        
        this.effects.delete(id);
        this.saveEffects();
        this.notifyListeners();

        return true;
    }

    /**
     * Apply effect changes to user object
     */
    private async applyEffectToUser(effect: CustomEffect, user: User): Promise<User> {
        let updatedUser = { ...user };
        
        switch (effect.type) {
            case 'token_boost':
                // Add tokens by reducing usage
                if (effect.config.tokenAmount) {
                    const currentUsage = user.dailyTokenUsage || 0;
                    updatedUser.dailyTokenUsage = Math.max(0, currentUsage - effect.config.tokenAmount);
                }
                break;
                
            case 'set_rank':
                // Set specific rank
                if (effect.config.targetTier) {
                    const isPerm = effect.config.isPermanent !== false;
                    
                    if (effect.config.targetTier === 'premium') {
                        updatedUser.isPremium = true;
                        if (!isPerm) {
                            const expiry = new Date();
                            expiry.setTime(expiry.getTime() + effect.durationMinutes * 60 * 1000);
                            updatedUser.premiumExpiryDate = expiry.toISOString();
                            updatedUser.premiumStartDate = new Date().toISOString();
                        } else {
                            updatedUser.premiumExpiryDate = undefined;
                        }
                        updatedUser.isMaxTier = false;
                    } else if (effect.config.targetTier === 'max') {
                        updatedUser.isPremium = true;
                        updatedUser.isMaxTier = true;
                        if (!isPerm) {
                            const expiry = new Date();
                            expiry.setTime(expiry.getTime() + effect.durationMinutes * 60 * 1000);
                            updatedUser.premiumExpiryDate = expiry.toISOString();
                            updatedUser.premiumStartDate = new Date().toISOString();
                        } else {
                            updatedUser.premiumExpiryDate = undefined;
                        }
                    } else if (effect.config.targetTier === 'standard') {
                        updatedUser.isPremium = false;
                        updatedUser.isMaxTier = false;
                        updatedUser.premiumExpiryDate = undefined;
                        updatedUser.premiumStartDate = undefined;
                    }
                }
                break;
                
            case 'tier_promote':
                // Promote 1 level
                if (!user.isPremium) {
                    updatedUser.isPremium = true;
                    updatedUser.isMaxTier = false;
                } else if (!user.isMaxTier) {
                    updatedUser.isMaxTier = true;
                }
                
                if (effect.config.isPermanent !== true && effect.durationMinutes > 0) {
                    const expiry = new Date();
                    expiry.setTime(expiry.getTime() + effect.durationMinutes * 60 * 1000);
                    updatedUser.premiumExpiryDate = expiry.toISOString();
                    updatedUser.premiumStartDate = new Date().toISOString();
                }
                break;
                
            case 'tier_demote':
                // Demote 1 level
                if (user.isMaxTier) {
                    updatedUser.isMaxTier = false;
                } else if (user.isPremium) {
                    updatedUser.isPremium = false;
                    updatedUser.premiumExpiryDate = undefined;
                    updatedUser.premiumStartDate = undefined;
                }
                break;
                
            case 'token_capacity_boost':
            case 'token_refresh_boost':
                // These are stored as active effects and applied via getUserActiveEffects
                // The token calculation logic in the app will read these effects
                break;
                
            case 'tier_upgrade':
                // Temporary tier upgrade
                if (effect.config.targetTier === 'premium') {
                    updatedUser.isPremium = true;
                    const expiry = new Date();
                    expiry.setTime(expiry.getTime() + effect.durationMinutes * 60 * 1000);
                    updatedUser.premiumExpiryDate = expiry.toISOString();
                    updatedUser.premiumStartDate = new Date().toISOString();
                } else if (effect.config.targetTier === 'max') {
                    updatedUser.isPremium = true;
                    updatedUser.isMaxTier = true;
                    const expiry = new Date();
                    expiry.setTime(expiry.getTime() + effect.durationMinutes * 60 * 1000);
                    updatedUser.premiumExpiryDate = expiry.toISOString();
                    updatedUser.premiumStartDate = new Date().toISOString();
                }
                break;
        }
        
        return updatedUser;
    }
    
    /**
     * Activate effect for user
     */
    async activateEffect(
        effectId: string,
        user: User,
        source: UserEffect['source'] = 'system',
        sourceId?: string
    ): Promise<EffectActivationResult> {
        const effect = this.effects.get(effectId);
        if (!effect) {
            return {
                success: false,
                message: 'Effect not found'
            };
        }

        if (!effect.isActive) {
            return {
                success: false,
                message: 'Effect is not active'
            };
        }

        // Calculate expiry
        const expiresAt = effect.durationMinutes === -1 
            ? null 
            : Date.now() + (effect.durationMinutes * 60 * 1000);

        // Get existing user effects
        let userEffects = this.userEffects.get(user.id) || [];

        // Check stacking
        const existingEffect = userEffects.find(e => e.effectId === effectId);
        if (existingEffect && !effect.stackable) {
            // Extend duration instead
            if (expiresAt && existingEffect.expiresAt) {
                existingEffect.expiresAt = Math.max(existingEffect.expiresAt, expiresAt);
            }
        } else if (existingEffect && effect.stackable) {
            // Add stack
            if (!effect.stackLimit || existingEffect.stacks < effect.stackLimit) {
                existingEffect.stacks++;
            } else {
                return {
                    success: false,
                    message: 'Maximum stacks reached'
                };
            }
        } else {
            // Add new effect
            userEffects.push({
                effectId,
                activatedAt: Date.now(),
                expiresAt,
                stacks: 1,
                source,
                sourceId
            });
        }

        this.userEffects.set(user.id, userEffects);
        this.saveUserEffects();
        
        // Apply immediate effects to user
        const updatedUser = await this.applyEffectToUser(effect, user);
        if (updatedUser !== user) {
            // Update user in database
            const { updateUser: saveUser } = await import('./firebaseService');
            await saveUser(user.id, updatedUser);
        }

        return {
            success: true,
            message: effect.config.displayMessage || `${effect.name} activated!`,
            effect,
            expiresAt: expiresAt || undefined,
            displayAnimation: effect.config.animationType
        };
    }

    /**
     * Get active effects for user
     */
    getUserActiveEffects(userId: string): Array<UserEffect & { effect: CustomEffect }> {
        const userEffects = this.userEffects.get(userId) || [];
        const now = Date.now();

        // Filter expired effects and enhance with effect details
        const activeEffects = userEffects
            .filter(ue => ue.expiresAt === null || ue.expiresAt > now)
            .map(ue => {
                const effect = this.effects.get(ue.effectId);
                return effect ? { ...ue, effect } : null;
            })
            .filter((e): e is UserEffect & { effect: CustomEffect } => e !== null);

        // Clean up expired effects
        const validEffects = userEffects.filter(ue => ue.expiresAt === null || ue.expiresAt > now);
        if (validEffects.length !== userEffects.length) {
            this.userEffects.set(userId, validEffects);
            this.saveUserEffects();
        }

        return activeEffects;
    }

    /**
     * Check if user has specific effect active
     */
    hasEffect(userId: string, effectId: string): boolean {
        const activeEffects = this.getUserActiveEffects(userId);
        return activeEffects.some(e => e.effectId === effectId);
    }

    /**
     * Check if user has effect of specific type
     */
    hasEffectType(userId: string, type: EffectType): boolean {
        const activeEffects = this.getUserActiveEffects(userId);
        return activeEffects.some(e => e.effect.type === type);
    }

    /**
     * Get cumulative value for effect type (e.g., total token boost)
     */
    getCumulativeEffectValue(userId: string, type: EffectType, configKey: keyof EffectConfig): number {
        const activeEffects = this.getUserActiveEffects(userId);
        return activeEffects
            .filter(e => e.effect.type === type)
            .reduce((total, e) => {
                const value = e.effect.config[configKey];
                if (typeof value === 'number') {
                    return total + (value * e.stacks);
                }
                return total;
            }, 0);
    }

    /**
     * Remove effect from user
     */
    removeUserEffect(userId: string, effectId: string): boolean {
        const userEffects = this.userEffects.get(userId);
        if (!userEffects) return false;

        const filtered = userEffects.filter(e => e.effectId !== effectId);
        if (filtered.length === userEffects.length) return false;

        this.userEffects.set(userId, filtered);
        this.saveUserEffects();
        return true;
    }

    /**
     * Clear all effects for user
     */
    clearUserEffects(userId: string): void {
        this.userEffects.delete(userId);
        this.saveUserEffects();
    }

    /**
     * Subscribe to effect changes
     */
    subscribe(callback: (effects: CustomEffect[]) => void): () => void {
        this.listeners.add(callback);
        callback(Array.from(this.effects.values()));
        return () => this.listeners.delete(callback);
    }

    /**
     * Notify all listeners
     */
    private notifyListeners(): void {
        const effects = Array.from(this.effects.values());
        this.listeners.forEach(callback => callback(effects));
    }

    /**
     * Link effect to promo code
     */
    linkToPromoCode(promoCode: PromoCode, effectIds: string[]): Partial<PromoCode> {
        return {
            ...promoCode,
            effect: 'feature_unlock' as const, // Use existing effect type
            featureUnlocks: effectIds
        };
    }
}

// Singleton instance
export const customEffectsService = new CustomEffectsService();

// Convenience functions
export const getAllEffects = () => customEffectsService.getAllEffects();
export const getActiveEffects = () => customEffectsService.getActiveEffects();
export const createEffect = (effect: Omit<CustomEffect, 'id' | 'createdAt'>) => 
    customEffectsService.createEffect(effect);
export const activateEffectForUser = (effectId: string, user: User, source?: UserEffect['source']) =>
    customEffectsService.activateEffect(effectId, user, source);
export const getUserEffects = (userId: string) => 
    customEffectsService.getUserActiveEffects(userId);
export const hasActiveEffect = (userId: string, effectId: string) =>
    customEffectsService.hasEffect(userId, effectId);
export const hasActiveEffectType = (userId: string, type: EffectType) =>
    customEffectsService.hasEffectType(userId, type);

export default customEffectsService;

